import React, { useState } from 'react';
import { Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import type { Table, Reservation } from '../../types';

interface TableConfigModalProps {
  table: Table;
  onUpdate: (updates: Partial<Table>) => void;
  onClose: () => void;
  isOpen: boolean;
  reservations?: Reservation[]; // candidates to seat (unassigned for shift/date)
  onSeat?: (reservationId: string) => void;
  currentReservation?: Reservation | null;
  onUnseat?: (reservationId: string) => void;
  zones?: string[];
}

export const TableConfigModal: React.FC<TableConfigModalProps> = ({
  table,
  onUpdate,
  onClose,
  isOpen,
  reservations = [],
  onSeat,
  currentReservation = null,
  onUnseat,
  zones = []
}) => {
  const [formData, setFormData] = useState({
    number: table.number,
    seats: table.seats,
    zone: table.zone,
    status: table.status
  });
  const [selectedReservationId, setSelectedReservationId] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Ρύθμιση Τραπεζιού {table.number}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Seat reservation section */}
          {onSeat && (
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="text-sm font-medium text-foreground">Κάθισε κράτηση σε Τ{table.number}</div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Επιλογή κράτησης (χωρίς τραπέζι)
                </label>
                <Select value={selectedReservationId} onValueChange={setSelectedReservationId}>
                  <SelectTrigger>
                    <SelectValue placeholder={reservations.length ? 'Διαλέξτε κράτηση' : 'Δεν υπάρχουν διαθέσιμες'} />
                  </SelectTrigger>
                  <SelectContent>
                    {reservations.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.time} • {r.customer?.name || 'Πελάτης'} • {r.party_size} άτομα
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="flex-1"
                  disabled={!selectedReservationId}
                  onClick={() => {
                    if (selectedReservationId && onSeat) onSeat(selectedReservationId);
                  }}
                >
                  Κάθισε τώρα
                </Button>
              </div>
            </div>
          )}

          {/* Unseat current reservation section */}
          {onUnseat && currentReservation && (
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="text-sm font-medium text-foreground">Αποδέσμευση τρέχουσας κράτησης</div>
              <div className="text-sm text-muted-foreground">
                {currentReservation.time} • {currentReservation.customer?.name || 'Πελάτης'} • {currentReservation.party_size} άτομα
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => onUnseat(currentReservation.id)}
                >
                  Αποδέσμευση τραπεζιού
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Αριθμός Τραπεζιού
              </label>
              <input
                type="number"
                value={formData.number}
                onChange={(e) => setFormData(prev => ({ ...prev, number: parseInt(e.target.value) }))}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter, and numbers
                  if (
                    [46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
                    (e.ctrlKey === true && [65, 67, 86, 88, 90].indexOf(e.keyCode) !== -1) ||
                    // Allow: home, end, left, right
                    (e.keyCode >= 35 && e.keyCode <= 39)
                  ) {
                    return;
                  }
                  // Ensure that it is a number and stop the keypress
                  if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                min="1"
                inputMode="numeric"
                pattern="[0-9]*"
                required
                autoFocus={false}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Αριθμός Καθούμενων
              </label>
              <input
                type="number"
                value={formData.seats}
                onChange={(e) => setFormData(prev => ({ ...prev, seats: parseInt(e.target.value) }))}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter, and numbers
                  if (
                    [46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
                    (e.ctrlKey === true && [65, 67, 86, 88, 90].indexOf(e.keyCode) !== -1) ||
                    // Allow: home, end, left, right
                    (e.keyCode >= 35 && e.keyCode <= 39)
                  ) {
                    return;
                  }
                  // Ensure that it is a number and stop the keypress
                  if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                min="1"
                max="20"
                inputMode="numeric"
                pattern="[0-9]*"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Ζώνη
            </label>
            <Select
              value={formData.zone || '__no_zone__'}
              onValueChange={(value) =>
                setFormData(prev => ({
                  ...prev,
                  zone: value === '__no_zone__' ? '' : value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__no_zone__">
                  Χωρίς ζώνη
                </SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone} value={zone}>
                    {zone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Κατάσταση
            </label>
            <Select value={formData.status} onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Διαθέσιμο</SelectItem>
                <SelectItem value="reserved">Κρατημένο</SelectItem>
                <SelectItem value="occupied">Κατειλημμένο</SelectItem>
                {/* <SelectItem value="maintenance">Σε συντήρηση</SelectItem> */}
                {/* <SelectItem value="cleaning">Καθαρισμός</SelectItem> */}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Ακύρωση
            </Button>
            <Button type="submit" className="flex-1">
              Ενημέρωση
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
